/**
 * v149 — Quick Promote ad cards. One renderer per promoted content type,
 * each mirroring the block people already know from bio pages:
 *   bio_page    → Trending page card (cover, avatar, followers, likes, views)
 *   product     → Featured product hero (first image, badges, price, CTA)
 *   paid_coupon → Paid coupon card, fully expanded (no truncation)
 *   video       → Tap-to-play Instagram Reel / YouTube Short, plays inline
 *
 * `onEngage(event)` fires on the click / play (view) that Linkola bills for.
 * `playing` + `onPlayingChange(on)` (optional) let a feed keep only one
 * video playing at a time.
 * `preview` renders the same card without navigation or tracking (wizard,
 * stats page).
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Heart, Lock, Play, ShoppingBag, Tag, Timer, Users, Gift, Instagram, Youtube } from "lucide-react";
import { externalLinkClickHandler } from "../../lib/openExternal";
import { withBackParam } from "../../lib/backTarget";
import { bioHref } from "../../lib/quickPromote";
import { InlineVideoEmbed } from "../InlineVideoEmbed";

const fmt = (n) => Number(n || 0).toLocaleString("en-IN");

function OutLink({ href, preview, onOpen, className, testid, children }) {
  if (preview) {
    return <div className={className} data-testid={testid}>{children}</div>;
  }
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      onClick={externalLinkClickHandler(href, () => onOpen?.())}
      className={className}
      data-testid={testid}
    >
      {children}
    </a>
  );
}

function BioLink({ slug, preview, onOpen, className, testid, children }) {
  const href = bioHref(slug);
  if (!preview && href.startsWith("/app/")) {
    return <Link to={href} onClick={() => onOpen?.()} className={className} data-testid={testid}>{children}</Link>;
  }
  return <OutLink href={href} preview={preview} onOpen={onOpen} className={className} testid={testid}>{children}</OutLink>;
}

function BioAdCard({ ad, preview, onEngage }) {
  const b = ad.bio || {};
  const initial = (b.name || "?").charAt(0).toUpperCase();
  return (
    <BioLink
      slug={b.slug}
      preview={preview}
      onOpen={() => onEngage?.("click")}
      className="group block border-2 border-ink bg-white text-ink shadow-brutalSm overflow-hidden relative"
      testid={`qp-ad-bio-${ad.ad_id}`}
    >
      <div className="h-32 sm:h-36 bg-gradient-to-br from-mint via-sun to-lavender relative border-b-2 border-ink">
        {b.cover_url && <img src={b.cover_url} alt="" className="absolute inset-0 w-full h-full object-cover" />}
        <div className="absolute -bottom-8 left-4 w-16 h-16 border-2 border-ink bg-white rounded-full overflow-hidden flex items-center justify-center font-display font-black text-2xl">
          {b.avatar_url ? <img src={b.avatar_url} alt={b.name} className="w-full h-full object-cover" /> : initial}
        </div>
      </div>
      <div className="pt-10 px-4 pb-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <p className="font-bold text-base break-words">{b.name}</p>
            <p className="text-[11px] text-ink/60 truncate">@{b.slug}</p>
          </div>
          <ArrowUpRight className="w-4 h-4 shrink-0 mt-1 opacity-40 group-hover:opacity-100" />
        </div>
        {b.tagline && <p className="text-xs text-ink/70 mt-2 line-clamp-3">{b.tagline}</p>}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-3 text-[11px] font-bold">
          <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" strokeWidth={3} />{fmt(b.followers_total)}</span>
          <span className="inline-flex items-center gap-1 text-coral"><Heart className="w-3 h-3 fill-current" strokeWidth={3} />{fmt(b.likes_total)}</span>
          <span className="ml-auto text-[10px] text-ink/50 uppercase">{fmt(b.views_30d)} views · 30d</span>
        </div>
        <span className="mt-3 w-full inline-flex items-center justify-center gap-1.5 border-2 border-ink bg-coral text-white px-4 py-2 text-sm font-bold">
          Visit page <ArrowUpRight className="w-4 h-4" />
        </span>
      </div>
    </BioLink>
  );
}

function shortDate(iso) {
  if (!iso) return "";
  try {
    const [y, m, d] = String(iso).slice(0, 10).split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch { return iso; }
}

function ProductAdCard({ ad, preview, onEngage }) {
  const p = ad.product || {};
  const kind = String(p.kind || "product").toLowerCase();
  const isService = kind === "service";
  const isTicket = kind === "ticket";
  const sym = { INR: "₹", USD: "$", EUR: "€", GBP: "£" }[String(p.currency || "INR").toUpperCase()] || "₹";
  const tiers = Array.isArray(p.ticket_tiers) ? p.ticket_tiers : [];
  const tierPrices = tiers.map((t) => Number(t.price || 0)).filter((n) => n > 0);
  const fromPrice = isTicket && tierPrices.length > 1 ? Math.min(...tierPrices) : null;
  const price = fromPrice != null ? fromPrice : Number(p.price || 0);
  const totalTickets = Number(p.total_tickets || 0);
  const ticketsLeft = isTicket && totalTickets > 0 ? Math.max(0, totalTickets - Number(p.tickets_sold || 0)) : null;
  const soldOut = ticketsLeft === 0;
  const images = p.image_urls?.length ? p.image_urls : (p.image_url ? [p.image_url] : []);
  const cta = soldOut ? "Sold out" : isService ? "Book now" : isTicket ? "Get tickets" : "Buy now";
  const eventDate = isTicket && p.event_date_from
    ? `${shortDate(p.event_date_from)}${p.event_date_to && p.event_date_to !== p.event_date_from ? ` – ${shortDate(p.event_date_to)}` : ""}${p.event_time ? ` · ${p.event_time}` : ""}`
    : "";
  return (
    <div className="border-4 border-ink bg-white text-ink shadow-brutal overflow-hidden" data-testid={`qp-ad-product-${ad.ad_id}`}>
      {/* One static image — ads stay strictly vertical: no sideways gallery, arrows or dots. */}
      <div className="w-full h-56 sm:h-72 border-b-4 border-ink bg-cream flex items-center justify-center p-1" data-testid={`qp-ad-product-img-${ad.ad_id}`}>
        {images[0]
          ? <img src={images[0]} alt="" loading="lazy" decoding="async" className="max-w-full max-h-full w-auto h-auto object-contain" />
          : <ShoppingBag className="w-14 h-14 text-ink/30" />}
      </div>
      <div className="p-4 sm:p-5 flex flex-col items-center text-center gap-2.5">
        <div className="flex items-center gap-1.5 flex-wrap justify-center">
          {isService && <span className="text-[10px] font-bold uppercase tracking-wider border border-ink px-1.5 py-0.5 bg-lavender">Service</span>}
          {isTicket && <span className="text-[10px] font-bold uppercase tracking-wider border border-ink px-1.5 py-0.5 bg-mint">Event</span>}
          {!isService && !isTicket && <span className="text-[10px] font-bold uppercase tracking-wider border border-ink px-1.5 py-0.5 bg-sun">Product</span>}
        </div>
        <h3 className="font-display font-black text-2xl tracking-tighter leading-tight break-words w-full">{p.title}</h3>
        {(eventDate || p.venue_name || ticketsLeft != null) && (
          <div className="flex flex-wrap items-center justify-center gap-1.5 text-[11px]">
            {eventDate && <span className="font-semibold">📅 {eventDate}</span>}
            {p.venue_name && <span className="text-ink/70">📍 {p.venue_name}</span>}
            {ticketsLeft != null && (
              <span className={`font-black uppercase px-1.5 py-0.5 border-2 border-ink ${soldOut ? "bg-coral text-white" : ticketsLeft <= 10 ? "bg-sun" : "bg-mint"}`}>
                {soldOut ? "Sold out" : `${ticketsLeft} left`}
              </span>
            )}
          </div>
        )}
        {isService && (Number(p.slots) > 0 || (p.available_days || []).length > 0) && (
          <p className="text-[11px] font-semibold">
            {Number(p.slots) > 0 && `🗓 ${p.slots} slot${p.slots > 1 ? "s" : ""} per day`}
            {(p.available_days || []).length > 0 && ` · ${p.available_days.map((d) => d.slice(0, 3)).join(", ")}`}
          </p>
        )}
        {p.description && <p className="text-sm text-ink/70 line-clamp-3 w-full">{p.description}</p>}
        {price > 0 && (
          <p className="font-display font-black text-3xl tracking-tighter">
            {fromPrice != null ? "from " : ""}{sym}{price.toFixed(0)}
            <span className="text-xs text-ink/50 font-bold ml-2">{p.currency || "INR"}</span>
          </p>
        )}
        {/* v149.6 — The ONLY Quick Promote change in this fix: the href
            gains `?back=<this bio page>`. The CTA opens `/p/{id}` in a
            new tab with rel="noreferrer", which leaves that tab with no
            history, no referrer and no opener — so without this param
            SellPage's Back arrow has nothing to return to and falls
            through to the Linkola landing page. Ad selection, tracking,
            eligibility and rendering are all untouched. */}
        <OutLink
          href={withBackParam(p.url)}
          preview={preview}
          onOpen={() => onEngage?.("click")}
          className={`w-full sm:w-auto border-2 border-ink px-5 py-2.5 font-bold text-sm shadow-brutalSm inline-flex items-center justify-center gap-2 ${soldOut ? "bg-white text-ink/50" : "bg-coral text-white"}`}
          testid={`qp-ad-product-cta-${ad.ad_id}`}
        >
          <ShoppingBag className="w-4 h-4" /> {cta}{price > 0 && !soldOut ? ` — ${sym}${price.toFixed(0)}${fromPrice != null ? "+" : ""}` : ""}
        </OutLink>
      </div>
    </div>
  );
}

function CouponAdCard({ ad, preview, onEngage }) {
  const c = ad.coupon || {};
  const price = Number(c.unlock_price || 0);
  const validity = c.end_date
    ? `Valid until ${new Date(c.end_date).toLocaleDateString()}`
    : c.valid_days_after_purchase ? `Redeem within ${c.valid_days_after_purchase} days of purchase` : null;
  return (
    <div className="border-2 border-ink bg-white text-ink shadow-brutalSm p-4 sm:p-5" data-testid={`qp-ad-coupon-${ad.ad_id}`}>
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 flex-shrink-0 border-2 border-ink bg-coral text-white flex items-center justify-center">
          <Lock className="w-5 h-5" strokeWidth={2.5} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-display font-black text-lg leading-tight break-words">{c.title || "Exclusive discount"}</p>
          {c.business_name && (
            <p className="text-xs text-ink/60 mt-0.5 break-words">
              {c.business_name}{c.business_category ? ` · ${c.business_category}` : ""}
            </p>
          )}
        </div>
        <span className="text-sm font-black flex-shrink-0 px-2 py-1 border-2 border-ink bg-coral text-white">
          {price > 0 ? `₹${price}` : "FREE"}
        </span>
      </div>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {c.voucher_mode && c.face_value ? (
          <span className="inline-flex items-center gap-1 border-2 border-ink bg-mint px-2 py-0.5 text-xs font-black">
            <Gift className="w-3.5 h-3.5" strokeWidth={2.5} /> ₹{Number(c.face_value).toFixed(0)} voucher for ₹{price}
          </span>
        ) : c.offer_summary ? (
          <span className="inline-flex items-center gap-1 border-2 border-ink bg-sun px-2 py-0.5 text-xs font-black">
            <Tag className="w-3.5 h-3.5" strokeWidth={2.5} /> {c.offer_summary}
          </span>
        ) : null}
      </div>
      {c.teaser_description && <p className="text-sm text-ink/80 mt-2 whitespace-pre-line break-words">{c.teaser_description}</p>}
      <div className="mt-2 space-y-0.5">
        {validity && <p className="text-[11px] text-ink/60 inline-flex items-center gap-1"><Timer className="w-3 h-3" /> {validity}</p>}
        {c.remaining != null && c.remaining < 50 && <p className="text-[11px] font-bold text-coral">Only {c.remaining} left!</p>}
      </div>
      <OutLink
        href={c.url}
        preview={preview}
        onOpen={() => onEngage?.("click")}
        className="mt-3 w-full border-2 border-ink bg-coral text-white px-4 py-2.5 font-bold text-sm shadow-brutalSm inline-flex items-center justify-center gap-2"
        testid={`qp-ad-coupon-cta-${ad.ad_id}`}
      >
        <Lock className="w-4 h-4" /> {price > 0 ? `Unlock for ₹${price}` : "Get this coupon"}
      </OutLink>
    </div>
  );
}

function VideoAdCard({ ad, preview, onEngage, playing: playingProp, onPlayingChange }) {
  const v = ad.video || {};
  const [localPlaying, setLocalPlaying] = useState(false);
  const controlled = typeof playingProp === "boolean";
  const playing = controlled ? playingProp : localPlaying;
  const vertical = v.platform === "instagram" || v.is_short;
  const PlatformIcon = v.platform === "instagram" ? Instagram : Youtube;
  const start = () => {
    if (!preview) onEngage?.("play");
    if (controlled) onPlayingChange?.(true); else setLocalPlaying(true);
  };
  return (
    <div className="border-2 border-ink bg-white text-ink shadow-brutalSm overflow-hidden" data-testid={`qp-ad-video-${ad.ad_id}`}>
      <div className="mx-auto w-full" style={{ maxWidth: vertical ? 360 : undefined }}>
        {playing ? (
          <InlineVideoEmbed
            url={v.url}
            autoplay
            aspect={v.platform === "youtube" && v.is_short ? "vertical" : null}
            surface="quick_promote"
            className="!border-0"
            testid={`qp-ad-video-player-${ad.ad_id}`}
          />
        ) : (
          <button
            type="button"
            onClick={start}
            className="relative block w-full bg-ink overflow-hidden"
            style={{ aspectRatio: vertical ? "4 / 5" : "16 / 9" }}
            aria-label={`Play ${ad.title || "video"}`}
            data-testid={`qp-ad-video-play-${ad.ad_id}`}
          >
            {v.thumbnail_url ? (
              <img src={v.thumbnail_url} alt="" className="absolute inset-0 w-full h-full object-cover opacity-90" />
            ) : (
              <div className="absolute inset-0 bg-gradient-to-br from-coral via-sun to-lavender" />
            )}
            <span className="absolute inset-0 bg-black/20" />
            <span className="absolute top-2 left-2 inline-flex items-center gap-1 border-2 border-ink bg-white px-1.5 py-0.5 text-[10px] font-black uppercase">
              <PlatformIcon className="w-3 h-3" /> {v.platform === "instagram" ? "Reel" : v.is_short ? "Short" : "Video"}
            </span>
            <span className="absolute inset-0 flex items-center justify-center">
              <span className="w-16 h-16 rounded-full border-2 border-ink bg-white flex items-center justify-center shadow-brutalSm">
                <Play className="w-7 h-7 fill-ink ml-1" />
              </span>
            </span>
          </button>
        )}
      </div>
      <div className="px-4 py-3 border-t-2 border-ink">
        <p className="font-bold text-base break-words">{ad.title}</p>
        <p className="text-xs text-ink/60 break-words">by {ad.advertiser?.name}</p>
      </div>
    </div>
  );
}

export default function PromoteAdCard({ ad, preview = false, onEngage, playing, onPlayingChange }) {
  if (!ad) return null;
  let card = null;
  if (ad.content_type === "bio_page") card = <BioAdCard ad={ad} preview={preview} onEngage={onEngage} />;
  else if (ad.content_type === "product") card = <ProductAdCard ad={ad} preview={preview} onEngage={onEngage} />;
  else if (ad.content_type === "paid_coupon") card = <CouponAdCard ad={ad} preview={preview} onEngage={onEngage} />;
  else if (ad.content_type === "video") {
    card = <VideoAdCard ad={ad} preview={preview} onEngage={onEngage} playing={playing} onPlayingChange={onPlayingChange} />;
  }
  if (!card) return null;
  return (
    <div className="space-y-2" data-testid={`qp-ad-${ad.ad_id}`}>
      {card}
      {ad.show_bio_link && ad.advertiser?.slug && (
        <BioLink
          slug={ad.advertiser.slug}
          preview={preview}
          onOpen={() => onEngage?.("click")}
          className="w-full inline-flex items-center justify-center gap-1.5 border-2 border-ink bg-white text-ink px-4 py-2 text-xs font-bold shadow-brutalSm"
          testid={`qp-ad-bio-link-${ad.ad_id}`}
        >
          Visit {ad.advertiser.name}&apos;s page <ArrowUpRight className="w-3.5 h-3.5" />
        </BioLink>
      )}
    </div>
  );
}
